// lets do this 
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar/Sidebar';
// import ThemeToggle from './components/ThemeToggle/ThemeToggle';

// Import Feedback component
import Feedback from './components/Feedback/Feedback';
import Home from './pages/Home/Home';
import { Menu, ChevronRight, Home as HomeIcon } from 'lucide-react';
import ErrorBoundary from './components/common/ErrorBoundary';

import PrivacyPolicy from './pages/Website/PrivacyPolicy/PrivacyPolicy';
import Landing from './pages/Website/Landing/Landing'; 
import NotFound from './pages/Website/NotFound/NotFound';
import FAQ from './pages/Website/FAQ/FAQ';
import AboutUs from './pages/Website/AboutUs/AboutUs';
import TermsOfService from './pages/Website/TermsOfService/TermsOfService';
import RefundPolicy from './pages/Website/RefundPolicy/RefundPolicy';
import { toolRoutes } from './pages/AITools/AITools';  // Import tool routes
import Contact from './pages/Website/Contact/Contact';  

// Create a Protected Route wrapper component
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    // Redirect to landing page with the attempted path in state
    return <Navigate to="/" state={{ from: location, requireAuth: true }} replace />;
  }

  return children;
}

// Mobile breadcrumb component
const MobileBreadcrumb = ({ currentPath }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Convert path to readable format
  const getPageTitle = (path) => {
    if (path === '/app' || path === '/app/') return 'Home';
    
    // Get the last part of the path
    const parts = path.split('/');
    const lastPart = parts[parts.length - 1];
    
    // Handle tool routes
    if (parts.includes('tools')) {
      const tool = toolRoutes.find(route => route.id === lastPart);
      return tool ? tool.name : 'Tool';
    }
    
    // Convert kebab-case to Title Case (e.g., "my-tool" to "My Tool")
    return lastPart
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  return (
    <div className="w-full bg-white border-b border-[#E6EFFD] px-4 py-3 flex items-center justify-between">
      <div className="flex items-center">
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 mr-2 text-[#5E60CE] rounded-full hover:bg-[#F0F7FF]"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center">
          <Link to="/app" className="text-[#5E60CE]">
            <HomeIcon className="w-4 h-4" />
          </Link>
          {currentPath !== '/app' && currentPath !== '/app/' && (
            <>
              <ChevronRight className="w-4 h-4 mx-1 text-gray-400" />
              <span className="text-[#5E60CE] font-medium">{getPageTitle(currentPath)}</span>
            </>
          )}
        </div>
      </div>
      
      {/* Mobile slide-in menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMenuOpen(false)}>
          <div className="absolute top-0 left-0 bottom-0 w-16 bg-white shadow-lg" onClick={e => e.stopPropagation()}>
            <Sidebar forceCollapsed={true} />
          </div>
        </div>
      )}
    </div>
  );
};

function AppLayout({ children }) {
  const location = useLocation();
  
  return (
    <div className="flex flex-col md:flex-row h-screen">
      {/* Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      
      {/* Mobile breadcrumb - only visible on mobile */}
      <div className="md:hidden">
        <MobileBreadcrumb currentPath={location.pathname} />
      </div>
      
      {/* Main content area */}
      <div className="flex-grow flex flex-col overflow-hidden relative">
        <main className="flex-grow flex flex-col items-center overflow-y-auto">
          <div className="w-full flex flex-col items-stretch">
            {/* Wrap children with ErrorBoundary */}
            <ErrorBoundary componentName="MainContent">
              {children}
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';

  return (
    <ErrorBoundary componentName="Application">
      <AuthProvider>
        {/* Render Feedback component outside of Routes to make it omnipresent */}
        <Feedback />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={<Contact />} />
          {/* App Routes - All prefixed with /app */}
          <Route
            path="/app/*"
            element={
              <AppLayout>
                <Suspense fallback={<div>Loading...</div>}>
                  <Routes>
                    {/* Main /app route - Public */}
                    <Route 
                      index 
                      element={<Home />} 
                    />  
                    
                    {/* Protected Tool Routes */}
                    {toolRoutes.map(route => (
                      <Route
                        key={route.id}
                        path={`tools/${route.id}`}
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<div>Loading...</div>}>
                              <ErrorBoundary componentName={route.id}>
                                <route.component />
                              </ErrorBoundary>
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                    ))}

                    {/* Protected FAQ Route */}
                    <Route 
                      path="faq" 
                      element={
                        <ProtectedRoute>
                          <FAQ />
                        </ProtectedRoute>
                      } 
                    />

                    {/* 404 for app routes */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AppLayout>
            }
          />

          {/* Global 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
